import { HttpErrorResponse } from '@angular/common/http';

export interface EnhancedError extends HttpErrorResponse {
  isNetworkError?: boolean;
  userFriendlyMessage?: string;
  originalError?: unknown;
}

export class ErrorHandlerUtility {

  static isNetworkError(error: unknown): boolean {
    const err: HttpErrorResponse = error as HttpErrorResponse;
    
    if (!err.status || err.status === 0) {
      return true;
    }
    
    if (err.message) {
      const networkErrorPatterns: string[] = [
        'network',
        'failed to fetch',
        'net::err',
        'connection',
        'timeout',
        'offline',
        'unreachable',
        'dns'
      ];
      
      const lowerMessage: string = err.message.toLowerCase();
      return networkErrorPatterns.some(pattern => lowerMessage.includes(pattern));
    }
    
    if (err.type && err.type.toString() === 'error' && !err.status) {
      return true;
    }
    
    return false;
  }
  
  static getUserFriendlyMessage(error: unknown): string {
    const err: HttpErrorResponse = error as HttpErrorResponse;
    // Network errors
    if (ErrorHandlerUtility.isNetworkError(error)) {
      return 'Network connection issue detected. Please check your internet connection and try again.';
    }
    
    // HTTP status code errors
    switch (err.status) {
      case 400:
        return err.error?.message || 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return err.error?.message || 'This action conflicts with existing data.';
      case 429:
        return 'Too many requests. Please wait a moment before trying again.';
      case 500:
        return 'Server error occurred. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again in a few moments.';
      default:
        return err.error?.message || 'An unexpected error occurred. Please try again.';
    }
  }

  static enhanceError(error: unknown): EnhancedError {
    const enhanced: EnhancedError = error as EnhancedError;
    enhanced.isNetworkError = ErrorHandlerUtility.isNetworkError(error);
    enhanced.userFriendlyMessage = ErrorHandlerUtility.getUserFriendlyMessage(error);
    enhanced.originalError = error;
    
    return enhanced;
  }
  
  static getDisplayMessage(error: unknown, fallbackMessage?: string): string {
    const err: EnhancedError = error as EnhancedError;
    if (err.userFriendlyMessage) {
      return err.userFriendlyMessage;
    }
    
    if (err.error?.message) {
      return err.error.message;
    }
    
    const friendlyMessage: string = ErrorHandlerUtility.getUserFriendlyMessage(error);
    if (friendlyMessage !== 'An unexpected error occurred. Please try again.') {
      return friendlyMessage;
    }
    
    return fallbackMessage || 'An unexpected error occurred. Please try again.';
  }
}
